// src/pages/storePages/manageStore/ManageStorePayment.jsx
import { useEffect, useState } from "react";
import {
  storeGetPaymentInfo,
  storeUpdatePaymentInfo,
} from "../../../storeAPIs/StorePaymentApis";
import "../../../static/storecss/manageStore/ManageStorePayment.css";

const EMPTY = {
  mpesa_till_number: "",
  mpesa_instructions: "",
  bank_details: "",
  bank_instructions: "",
  paypal_details: "",
  paypal_instructions: "",
};

export default function ManageStorePayment() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    storeGetPaymentInfo()
      .then((res) => setForm((f) => ({ ...f, ...res.data })))
      .catch((err) => setError(err.message || "Failed to load settings."))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await storeUpdatePaymentInfo(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="msp-page">
        <div className="msp-loading">
          <span className="msp-spinner" />
          Loading payment info…
        </div>
      </div>
    );
  }

  return (
    <form className="msp-page" onSubmit={handleSubmit}>
      {/* ── Header ── */}
      <header className="msp-header">
        <div className="msp-header-text">
          <h1>Payment info</h1>
          <p>Where customers send their payments at checkout.</p>
        </div>

        <div className="msp-header-actions">
          {saved && (
            <span className="msp-saved">
              <i className="fa-solid fa-check" aria-hidden="true" />
              Saved
            </span>
          )}
          <button type="submit" className="msp-save" disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </header>

      {error && <div className="msp-alert">{error}</div>}

      {/* ── Cards grid ── */}
      <div className="msp-grid">
        {/* ── M-Pesa ── */}
        <section className="msp-card msp-card--mpesa">
          <header className="msp-card-head">
            <span className="msp-card-icon">
              <i className="fa-solid fa-mobile-screen-button" />
            </span>
            <div className="msp-card-head-text">
              <h2>M-Pesa</h2>
              <p>Mobile money via till or paybill</p>
            </div>
          </header>

          <div className="msp-card-body">
            <div className="msp-field">
              <label htmlFor="mpesa_till">Till number</label>
              <input
                id="mpesa_till"
                type="text"
                value={form.mpesa_till_number}
                onChange={(e) => update("mpesa_till_number", e.target.value)}
                placeholder="e.g. 522522"
              />
              <small>Shown to the customer at checkout.</small>
            </div>

            <div className="msp-field">
              <label htmlFor="mpesa_instructions">Instructions</label>
              <textarea
                id="mpesa_instructions"
                rows="3"
                value={form.mpesa_instructions}
                onChange={(e) =>
                  update("mpesa_instructions", e.target.value)
                }
                placeholder="Extra guidance shown to the customer."
              />
            </div>
          </div>
        </section>

        {/* ── Bank ── */}
        <section className="msp-card msp-card--bank">
          <header className="msp-card-head">
            <span className="msp-card-icon">
              <i className="fa-solid fa-building-columns" />
            </span>
            <div className="msp-card-head-text">
              <h2>Bank transfer</h2>
              <p>Direct bank deposit or wire</p>
            </div>
          </header>

          <div className="msp-card-body">
            <div className="msp-field">
              <label htmlFor="bank_details">Bank details</label>
              <textarea
                id="bank_details"
                rows="4"
                value={form.bank_details}
                onChange={(e) => update("bank_details", e.target.value)}
                placeholder={
                  "Bank name: …\nAccount name: …\nAccount number: …\nBranch: …"
                }
              />
            </div>

            <div className="msp-field">
              <label htmlFor="bank_instructions">Instructions</label>
              <textarea
                id="bank_instructions"
                rows="3"
                value={form.bank_instructions}
                onChange={(e) => update("bank_instructions", e.target.value)}
                placeholder="Extra guidance shown to the customer."
              />
            </div>
          </div>
        </section>

        {/* ── PayPal ── */}
        <section className="msp-card msp-card--paypal">
          <header className="msp-card-head">
            <span className="msp-card-icon">
              <i className="fa-brands fa-paypal" />
            </span>
            <div className="msp-card-head-text">
              <h2>PayPal</h2>
              <p>International card payments</p>
            </div>
          </header>

          <div className="msp-card-body">
            <div className="msp-field">
              <label htmlFor="paypal_details">PayPal email or link</label>
              <input
                id="paypal_details"
                type="text"
                value={form.paypal_details}
                onChange={(e) => update("paypal_details", e.target.value)}
                placeholder="you@example.com or paypal.me/you"
              />
            </div>

            <div className="msp-field">
              <label htmlFor="paypal_instructions">Instructions</label>
              <textarea
                id="paypal_instructions"
                rows="3"
                value={form.paypal_instructions}
                onChange={(e) =>
                  update("paypal_instructions", e.target.value)
                }
                placeholder="Extra guidance shown to the customer."
              />
            </div>
          </div>
        </section>
      </div>

      {/* ── Footer strip — note only ── */}
      <footer className="msp-footer">
        <span className="msp-footer-note">
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          Changes affect what customers see at checkout.
        </span>
      </footer>
    </form>
  );
}